import { useState } from "react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";
import { Feather } from "lucide-react";

const AUTH_BG = "https://images.unsplash.com/photo-1706541972715-41ea84cf1d1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxsaWdodCUyMGNvbWluZyUyMHRocm91Z2glMjB3aW5kb3clMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgyMjIwMjEyfDA&ixlib=rb-4.1.0&q=85";

function submitLabel(busy, mode) {
  if (busy) return "Please wait…";
  return mode === "login" ? "Enter" : "Create account";
}

export default function Auth() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      toast.success(mode === "login" ? "Welcome back." : "Your journey begins.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="absolute top-5 right-5 z-10">
        <ThemeToggle />
      </div>
      {/* Image side */}
      <div className="relative hidden lg:block">
        <img src={AUTH_BG} alt="Light through arched windows" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[hsl(45_30%_96%/0.35)] dark:bg-[hsl(160_14%_8%/0.62)]" />
        <div className="absolute inset-0 flex flex-col justify-end p-16">
          <p className="label-overline mb-4">A spiritual practice</p>
          <h2 className="font-scripture italic text-4xl leading-snug text-primary max-w-md">
            "Be still, and know that I am God."
          </h2>
          <p className="mt-3 text-sm text-primary/70">Psalm 46:10</p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center px-6 py-16 sm:px-12 bg-background">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-3 mb-12">
            <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              <Feather className="w-5 h-5" />
            </span>
            <span className="font-heading text-4xl font-semibold tracking-tight">Attune</span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none mb-3">
            {mode === "login" ? "Return to stillness" : "Begin your attunement"}
          </h1>
          <p className="text-muted-foreground mb-10 text-sm leading-relaxed">
            Align your focus with God's will and attune your heart to the Holy Spirit.
          </p>

          <form onSubmit={submit} className="space-y-7">
            {mode === "register" && (
              <div>
                <label className="label-overline">Name</label>
                <input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required
                  className="w-full bg-transparent border-b border-border py-2 mt-1 focus:outline-none focus:border-primary transition-colors" placeholder="Your name" />
              </div>
            )}
            <div>
              <label className="label-overline">Email</label>
              <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full bg-transparent border-b border-border py-2 mt-1 focus:outline-none focus:border-primary transition-colors" placeholder="you@email.com" />
            </div>
            <div>
              <label className="label-overline">Password</label>
              <input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                className="w-full bg-transparent border-b border-border py-2 mt-1 focus:outline-none focus:border-primary transition-colors" placeholder="••••••••" />
            </div>
            <button data-testid="auth-submit-button" type="submit" disabled={busy}
              className="w-full bg-primary text-primary-foreground rounded-sm py-3.5 mt-2 hover:bg-primary/90 transition-colors disabled:opacity-60 tracking-wide">
              {submitLabel(busy, mode)}
            </button>
          </form>

          <p className="mt-10 text-sm text-muted-foreground">
            {mode === "login" ? "New to Attune?" : "Already have an account?"}{" "}
            <button data-testid="auth-toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-primary underline underline-offset-4 hover:text-accent transition-colors">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
